import { Controller, useFormContext } from 'react-hook-form';
import { useRef, useContext, useState } from 'react';
import Dropzone, { DropzoneRef, FileRejection } from 'react-dropzone';
import toast from 'react-hot-toast';
import {
    Card,
    CardBody,
    CardTitle,
    FormGroup,
    Input,
    Label,
    ListGroup,
    ListGroupItem,
    Button,
    Modal,
    ModalBody,
    Row,
    Col,
} from 'reactstrap';
import { CheckBox } from '~/shared/components/form/CheckBox';
import { Vendor } from '~/types/fundrequests';
import { FundRequestContext } from '~/context/fundrequests';
import achExampleImage from './ach-example.png';

const achAccountTypeOptions = [
    { label: 'Checking', value: 'CHECKING' },
    {
        label: 'Savings',
        value: 'SAVINGS',
    },
];

const achDocumentTypeOptions = [
    { label: 'A voided check', value: 'VOID_CHECK' },
    {
        label: 'Letter or screenshot from bank stating account and routing number',
        value: 'BANK_LETTER',
    },
];
function AchInfoForm() {
    const {
        control,
        setValue,
        watch,
        formState: { errors },
    } = useFormContext();
    const [{ selectedPayee, fundRequest }] = useContext(FundRequestContext);
    const dropzoneRef = useRef<DropzoneRef>(null);
    const maxFileSizeMB = 5;
    const [isExampleModalOpen, setIsExampleModalOpen] = useState(false);

    const agreement_signed = watch('ach_info.agreement_signed');
    const achFile = watch('ach_info.document');
    const accountType = watch('ach_info.account_type');
    const documentType = watch('ach_info.document_type');
    const existingAchDocumentPath = watch('ach_info.path');

    // When resubmitting a fund request that already has an ACH request on file,
    // routing/account numbers are preserved server-side (and encrypted, so never
    // returned to the form) and the document is already uploaded. Treat those
    // fields as optional here so the form is valid and the Resubmit button is
    // enabled. Backend (`FundRequestDetail/index.tsx`) enforces the same rule.
    const isExistingAchRequest = !!fundRequest?.ach_request;
    const isBankNumberRequired = !isExistingAchRequest;
    const isDocumentRequired = !isExistingAchRequest && !existingAchDocumentPath;

    const handlePreventPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        toast.error('Copying and pasting is not allowed for this field');
    };

    const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (fileRejections.length > 0) {
            toast.error(
                `File must be a PDF or image and less than ${maxFileSizeMB} MB`
            );
            return;
        }

        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0]; // Only take the first file
            setValue('ach_info.document', file, { shouldValidate: true });
        }
    };

    return (
        <>
            {selectedPayee && (
                <Card
                    style={{
                        marginBottom: 20,
                        height: '100%',
                        border: '4px solid red',
                    }}
                >
                    <CardBody>
                        <CardTitle
                            tag='h5'
                            style={{ fontSize: '1.3rem', fontWeight: 'bold' }}
                        >
                            {'Direct Deposit (ACH) Information'}
                        </CardTitle>

                        <div style={{ marginTop: 5 }}>
                            <Label>
                                Account Type <span className='required'>*</span>
                            </Label>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    alignItems: 'center',
                                }}
                            >
                                {achAccountTypeOptions.map((option) => (
                                    <FormGroup
                                        check
                                        key={option.value}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Label
                                            check
                                            style={{ marginRight: '0.5rem' }}
                                        >
                                            <Controller
                                                name='ach_info.account_type'
                                                control={control}
                                                rules={{ required: 'Please select an account type' }}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        type='radio'
                                                        value={option.value}
                                                        checked={
                                                            field.value ===
                                                            option.value
                                                        }
                                                        style={{
                                                            marginTop: '3px',
                                                        }}
                                                        onChange={() =>
                                                            setValue(
                                                                'ach_info.account_type',
                                                                option.value,
                                                                { shouldValidate: true }
                                                            )
                                                        }
                                                    />
                                                )}
                                            />
                                            {option.label}
                                        </Label>
                                    </FormGroup>
                                ))}
                            </div>
                        </div>

                        {/* Routing Number and Account Number Fields */}
                        <Row className='mt-3'>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>
                                        Routing Number <span className='required'>*</span>
                                    </Label>
                                    <Controller
                                        name='ach_info.routing_number'
                                        control={control}
                                        rules={{
                                            required: isBankNumberRequired
                                                ? 'Routing number is required'
                                                : false,
                                            pattern: {
                                                value: /^[0-9]{9}$/,
                                                message: 'Routing number must be exactly 9 digits'
                                            }
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type='text'
                                                placeholder='444444444'
                                                maxLength={9}
                                                invalid={!!(errors?.ach_info as any)?.routing_number}
                                            />
                                        )}
                                    />
                                    {(errors?.ach_info as any)?.routing_number && (
                                        <div className='text-danger small mt-1'>
                                            {((errors.ach_info as any).routing_number.message as string)}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>
                                        Confirm Routing Number <span className='required'>*</span>
                                    </Label>
                                    <Controller
                                        name='ach_info.confirm_routing_number'
                                        control={control}
                                        rules={{
                                            required: isBankNumberRequired
                                                ? 'Please confirm routing number'
                                                : false,
                                            validate: (value) =>
                                                value === watch('ach_info.routing_number') ||
                                                'Routing numbers do not match'
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type='text'
                                                placeholder='444444444'
                                                maxLength={9}
                                                onPaste={handlePreventPaste}
                                                onCopy={handlePreventPaste}
                                                invalid={!!(errors?.ach_info as any)?.confirm_routing_number}
                                            />
                                        )}
                                    />
                                    {(errors?.ach_info as any)?.confirm_routing_number && (
                                        <div className='text-danger small mt-1'>
                                            {((errors.ach_info as any).confirm_routing_number.message as string)}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>
                                        Account Number <span className='required'>*</span>
                                    </Label>
                                    <Controller
                                        name='ach_info.account_number'
                                        control={control}
                                        rules={{
                                            required: isBankNumberRequired
                                                ? 'Account number is required'
                                                : false,
                                            pattern: {
                                                value: /^[0-9]+$/,
                                                message: 'Account number must contain only numbers'
                                            },
                                            minLength: {
                                                value: 4,
                                                message: 'Account number must be at least 4 digits'
                                            },
                                            maxLength: {
                                                value: 17,
                                                message: 'Account number must not exceed 17 digits'
                                            }
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type='text'
                                                placeholder='OOOOOOOOOOOO'
                                                maxLength={17}
                                                invalid={!!(errors?.ach_info as any)?.account_number}
                                            />
                                        )}
                                    />
                                    {(errors?.ach_info as any)?.account_number && (
                                        <div className='text-danger small mt-1'>
                                            {((errors.ach_info as any).account_number.message as string)}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>
                                        Confirm Account Number <span className='required'>*</span>
                                    </Label>
                                    <Controller
                                        name='ach_info.confirm_account_number'
                                        control={control}
                                        rules={{
                                            required: isBankNumberRequired
                                                ? 'Please confirm account number'
                                                : false,
                                            validate: (value) =>
                                                value === watch('ach_info.account_number') ||
                                                'Account numbers do not match'
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type='text'
                                                placeholder='OOOOOOOOOOOO'
                                                maxLength={17}
                                                onPaste={handlePreventPaste}
                                                onCopy={handlePreventPaste}
                                                invalid={!!(errors?.ach_info as any)?.confirm_account_number}
                                            />
                                        )}
                                    />
                                    {(errors?.ach_info as any)?.confirm_account_number && (
                                        <div className='text-danger small mt-1'>
                                            {((errors.ach_info as any).confirm_account_number.message as string)}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className='mb-3'>
                            <Button
                                color='link'
                                className='p-0'
                                onClick={() => setIsExampleModalOpen(true)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    textDecoration: 'none',
                                    color: '#00A8A8',
                                }}
                            >
                                <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
                                Where do I find my Routing/Account numbers?
                            </Button>
                        </div>

                        <div style={{ marginTop: 15 }}>
                            <Label>
                                I am providing one of the following:{' '}
                                <span className='required'>*</span>
                            </Label>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    alignItems: 'center',
                                }}
                            >
                                {achDocumentTypeOptions.map((option) => (
                                    <FormGroup
                                        check
                                        key={option.value}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Label
                                            check
                                            style={{ marginRight: '0.5rem' }}
                                        >
                                            <Controller
                                                name='ach_info.document_type'
                                                control={control}
                                                rules={{ required: 'Please select a document type' }}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        type='radio'
                                                        value={option.value}
                                                        checked={
                                                            field.value ===
                                                            option.value
                                                        }
                                                        style={{
                                                            marginTop: '3px',
                                                        }}
                                                        onChange={() =>
                                                            setValue(
                                                                'ach_info.document_type',
                                                                option.value,
                                                                { shouldValidate: true }
                                                            )
                                                        }
                                                    />
                                                )}
                                            />
                                            {option.label}
                                        </Label>
                                    </FormGroup>
                                ))}
                            </div>
                        </div>
                        <div style={{ marginTop: 15 }}>
                            <Label>Upload Document <span className='required'>*</span></Label>
                            <Controller
                                name='ach_info.document'
                                control={control}
                                rules={{
                                    required: isDocumentRequired
                                        ? 'Please upload a voided check or bank letter'
                                        : false
                                }}
                                render={({ field: { value, onChange, ...field } }) => (
                                    <>
                                        <Dropzone
                                            onDrop={(acceptedFiles, fileRejections) =>
                                                handleDrop(acceptedFiles, fileRejections)
                                            }
                                            accept={{
                                                'application/pdf': ['.pdf'],
                                                'image/jpeg': ['.jpeg', '.jpg'],
                                                'image/png': ['.png']
                                            }}
                                            multiple={false}
                                            ref={dropzoneRef}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <div
                                                    {...getRootProps()}
                                                    style={{
                                                        border: `2px dashed ${(errors?.ach_info as any)?.document ? '#dc3545' : '#949599'}`,
                                                        padding: '20px',
                                                        backgroundColor: '#F0F6F6',
                                                        textAlign: 'center',
                                                        cursor: 'pointer',
                                                        borderRadius: '8px',
                                                    }}
                                                >
                                                    <input {...getInputProps()} />
                                                    <div>
                                                        <Button
                                                            color='outline-dark'
                                                            size='sm'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                dropzoneRef.current?.open();
                                                            }}
                                                        >
                                                            Upload File
                                                        </Button>
                                                        <p className='small mt-2 mb-0'>
                                                            Drag & drop a file here, or click to select
                                                        </p>
                                                        <p className='small mb-0' style={{ color: '#888888' }}>
                                                            Maximum upload file size: {maxFileSizeMB} MB
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </Dropzone>
                                        {achFile && (
                                            <div className='mt-2 d-flex align-items-center gap-2'>
                                                <span className='text-success'>
                                                    ✓ {achFile.name}
                                                </span>
                                                <Button
                                                    color='link'
                                                    size='sm'
                                                    onClick={() => {
                                                        setValue('ach_info.document', null, { shouldValidate: true });
                                                    }}
                                                    className='p-0 text-danger'
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        )}
                                        {!achFile && existingAchDocumentPath && (
                                            <div className='mt-2 d-flex align-items-center gap-2'>
                                                <span className='text-success'>
                                                    ✓ {watch('ach_info.filename') || 'Document on file'}
                                                </span>
                                                <span className='text-muted small'>
                                                    (upload a new file to replace)
                                                </span>
                                            </div>
                                        )}
                                        {(errors?.ach_info as any)?.document && (
                                            <div className='text-danger small mt-1'>
                                                {((errors.ach_info as any).document.message as string)}
                                            </div>
                                        )}
                                    </>
                                )}
                            />
                        </div>
                        <h5
                            className='mb-3 mt-3'
                            style={{ fontSize: '1.3rem', fontWeight: 'bold' }}
                        >
                            TERMS AND CONDITIONS FOR DIRECT DEPOSIT PAYMENTS
                        </h5>
                        <ListGroup flush>
                            <ListGroupItem>
                                1. The electronic funds transfer will be made to
                                the financial institution and account listed on
                                this form.
                            </ListGroupItem>
                            <ListGroupItem>
                                2. The information on this form is very
                                important. Payee understands that any change in
                                the information provided must be communicated by
                                an authorized representative of Payee in writing
                                to Help Hope Live in time to allow us to respond
                                to the change. Payee expressly agrees to
                                indemnify and hold harmless Help Hope Live from
                                any loss that may arise by reason of error,
                                mistake or fraud relating to the information
                                Payee has provided.
                            </ListGroupItem>
                            <ListGroupItem>
                                3. Help Hope Live has the right to adjust future
                                payments if payments made are found to be
                                duplicates, in excess of requirements,
                                fraudulently induced, or otherwise in error.
                            </ListGroupItem>
                            <ListGroupItem>
                                4. In the event that Help Hope Live or a banking
                                institution is notified that funds have been
                                deposited to payee's banking account in error,
                                payee authorizes the applicable banking
                                institution to return such funds to Help Hope
                                Live.
                            </ListGroupItem>
                        </ListGroup>
                        <FormGroup check className='mb-3 ps-0 mt-3'>
                            <CheckBox
                                name='ach_info.agreement_signed'
                                label='I agree to the Terms and Conditions laid out above *'
                                value={agreement_signed}
                                onChange={() => {
                                    setValue(
                                        'ach_info.agreement_signed',
                                        !agreement_signed
                                    );
                                }}
                            />
                        </FormGroup>
                    </CardBody>
                </Card>
            )}

            {/* ACH Example Modal */}
            <Modal 
                centered 
                isOpen={isExampleModalOpen} 
                toggle={() => setIsExampleModalOpen(false)}
                size='lg'
            >
                <ModalBody>
                    <h5 className='mb-3'>Where do I find my Routing/Account numbers?</h5>
                    <img 
                        src={achExampleImage} 
                        alt='ACH Routing and Account Number Example' 
                        style={{ width: '100%', height: 'auto' }}
                    />
                    <div className='mt-3 d-flex justify-content-end'>
                        <Button 
                            color='outline-dark' 
                            onClick={() => setIsExampleModalOpen(false)}
                        >
                            Close
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
}

export default AchInfoForm;
